import { Alert, AlertTitle } from '@cmg/design-system';
import React from 'react';

import {
  AttestationFormStatus,
  CertificateType,
  OfferingType,
  ProspectusDeliveryStatus,
} from '../../../../../../../../graphql';
import {
  AttestationFormStatusDisplayName,
  isAttestationStatusValid,
} from '../../../../../../../order-book/attestation/Attestation.model';
import { CertificateTypeLabels } from '../../../../../compliance/context/hooks/certificates/Certificates.model';
import { InterestLevel } from '../../../indication-form/types';
import {
  isMarketIndicationsForbidden,
  isNonSharesUnitIndicationsForbidden,
  isPercentageOfParNoLongerAccepted,
} from './ComplianceErrorBanner.model';

export type Props = Readonly<{
  offeringType: OfferingType | null;
  attestationStatus?: AttestationFormStatus;
  prospectusDeliveryStatus?: ProspectusDeliveryStatus;
  isMissingContacts: boolean;
  isLimitIndicationsOnlyEnabled: boolean;
  isIndicationPriceAsPercentageParEnabled: boolean;
  isOnlySharesUnitOnIndicationsEnabled: boolean;
  interestLevels: readonly InterestLevel[];
  certificateType: CertificateType | null;
}>;

const ComplianceErrorBanner: React.FC<Props> = ({
  offeringType,
  isMissingContacts,
  prospectusDeliveryStatus,
  attestationStatus,
  isLimitIndicationsOnlyEnabled,
  isIndicationPriceAsPercentageParEnabled,
  isOnlySharesUnitOnIndicationsEnabled,
  interestLevels,
  certificateType,
}) => {
  const attestationInvalid = attestationStatus && !isAttestationStatusValid(attestationStatus);
  const prospectusInvalid = prospectusDeliveryStatus === ProspectusDeliveryStatus.Failed;

  const marketIndicationsForbidden = isMarketIndicationsForbidden(
    isLimitIndicationsOnlyEnabled,
    interestLevels
  );

  const percentageOfParNoLongerAccepted = isPercentageOfParNoLongerAccepted(
    isIndicationPriceAsPercentageParEnabled,
    offeringType,
    interestLevels
  );

  const nonSharesUnitIndicationsForbidden = isNonSharesUnitIndicationsForbidden(
    isOnlySharesUnitOnIndicationsEnabled,
    interestLevels
  );

  if (
    !attestationInvalid &&
    !prospectusInvalid &&
    !isMissingContacts &&
    !marketIndicationsForbidden &&
    !percentageOfParNoLongerAccepted &&
    !nonSharesUnitIndicationsForbidden
  ) {
    return null;
  }

  return (
    <Alert severity="error" sx={{ mb: 1 }}>
      <AlertTitle>Action required for this indication</AlertTitle>

      {attestationInvalid && (
        <div>
          {[
            CertificateTypeLabels[certificateType ?? CertificateTypeLabels.FORM51305131],
            AttestationFormStatusDisplayName[attestationStatus],
          ].join(' ')}
        </div>
      )}
      {marketIndicationsForbidden && <div>Market IOIs are no longer allowed</div>}
      {nonSharesUnitIndicationsForbidden && <div>IOIs must be in Shares</div>}
      {isMissingContacts && <div>Investor contacts missing</div>}
      {prospectusInvalid && <div>Documentation Delivery Failed</div>}
      {percentageOfParNoLongerAccepted && <div>% of Par is no longer accepted</div>}
    </Alert>
  );
};

export default ComplianceErrorBanner;
